import { BusinessTypesMapping } from 'api/client/mapping/business-types-mapping';
import { AddTeamMemberModel, ChangeActiveRegionDirector, DeviationTeamRegionDirectorCreateDTO, DeviationTeamRegionDirectorUpdateDTO, DeviationTeamsClient, DeviationTeamUpdateDTO, IDeviationTeamDetailDTO, IDeviationTeamRegionDirectorDetailDTO, IDeviationTeamRegionDirectorUpdateBaseDTO, IDeviationTeamUpdateDTO, RemoveTeamMember } from 'api/swagger/ApiClient';
import { autoinject, BindingEngine, customElement, newInstance, computedFrom } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ValidationController, ValidationRules } from 'aurelia-validation';
import { watch } from 'aurelia-watch-decorator';
import { DialogHelper } from 'framework/aurelia_dialogs/dialog-helper';
import { BootstrapGroupRenderer } from 'framework/aurelia_tools/bootstrap-group-renderer';
import { cloneDeep } from 'framework/script_utilities/object-equalizer';
import { DeviationTeamDataModel, DeviationTeamModel } from 'model/forms/deviation-team-model';
import { SystemUserWrapper } from 'model/support-types';
import { ModelBase } from 'support/base_classes/model-base';
import { ToastFormat, ToastHelper, ToastType } from 'support/toaster';
import { ValidationHelper } from 'support/validation';
import { LookupsService } from 'services/lookups-service';
import { DeviationTeamsMapping } from 'api/client/mapping/deviation-teams-mapping';
import { UserContact } from 'model/business-types';
import { EventHelper } from 'framework/aurelia_tools/event-helper';
import { DeviationTeamRegionDirectorDataModel, DeviationTeamRegionDirectorModel } from 'model/forms/deviation-team-region-director-model';

@autoinject
@customElement('deviation-teams-panel')
export class DeviationTeamPanelCustomElement extends ModelBase<DeviationTeamDataModel> {
  @bindable deviationTeamDetailModel: DeviationTeamModel;
  backup: DeviationTeamDataModel = null;
  regionDirectors: DeviationTeamRegionDirectorModel[] = [];
  newEmptySystemUserWrapper: SystemUserWrapper = { user: null, userId: null };
  isMemberEditing = false;
  creatingNewRegionDirector = false;
  submitButtonVisible = false;
  cancelButtonVisible = false;

  constructor(private api: DeviationTeamsClient, private lookups: LookupsService, bindingEngine: BindingEngine, private dialogHelper: DialogHelper, private toastHelper: ToastHelper, private deviationTeamsMapping: DeviationTeamsMapping, private businessTypesMapping: BusinessTypesMapping, private validationHelper: ValidationHelper, @newInstance() private validationController: ValidationController, validationRenderer: BootstrapGroupRenderer, private eventHelper: EventHelper) {
    super(bindingEngine);
    validationRenderer.allowedParents = 'bs-form-input, bs-form-lookup';
    this.validationController.addRenderer(validationRenderer);
  }

  bind() {
    this.model = this.deviationTeamDetailModel.data;
    this.regionDirectors = this.deviationTeamDetailModel.regionDirectors.map(x => ({
      data: x,
      hasChanged: false,
      validationController: null
    } as DeviationTeamRegionDirectorModel));
    this.deviationTeamDetailModel.isEditing = () => this.changed || this.isMemberEditing || this.creatingNewRegionDirector || this.regionDirectors.some(x => x.hasChanged);
    super.bind();
    this.backup = cloneDeep(this.model);
  }

  modelChanged(newValue: DeviationTeamDataModel) {
    super.modelChanged(newValue);
    this.newEmptySystemUserWrapper = { user: null, userId: null } as SystemUserWrapper;
    ValidationRules
      .ensureObject<DeviationTeamDataModel>().required()
      .ensure(model => model.name).displayName('Team name').required()
      .ensure(model => model.emailAddress).satisfies((emailAddress: string | null, deviationTeam: DeviationTeamDataModel) => !deviationTeam.teamStrategy.hasEmailAddress || emailAddress != null).withMessage('Email Address is required.')
      .ensure(model => model.teamLeader).satisfies((teamLeader: UserContact | null, deviationTeam: DeviationTeamDataModel) => !deviationTeam.teamStrategy.hasTeamLeader || teamLeader != null).withMessage('Team Leader is required.')
      .on(this.model);
    this.validationController.reset();
    this.updateButons();
  }

  async submit() {
    await this.validationHelper.validate(this.validationController) && await this.save();
  }

  async cancel() {
    if (!this.changed || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      this.changed && this.rebindData(this.backup);
    }
  }

  private rebindData(dto: DeviationTeamDataModel) {
    this.deviationTeamDetailModel.data = dto;
    this.model = dto;
    this.backup = cloneDeep(this.model);
  }

  private async save() {
    const dto = await this.api.updateDeviationTeam(this.model.id, new DeviationTeamUpdateDTO(this.getSaveData(this.model)));
    this.rebindData(this.deviationTeamsMapping.mapToDeviationTeamDataModel(dto));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Deviation Team has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  getSaveData(model: DeviationTeamDataModel): IDeviationTeamUpdateDTO {
    return {
      name: model.name,
      emailAddress: model.emailAddress,
      teamLeaderUniqueId: model.teamLeader?.uniqueid
    }
  }

  addNewMember(item: SystemUserWrapper): Promise<void> {
    return this.api.addTeamMember(this.model.id, new AddTeamMemberModel({ teamMemberUniqueId: item.user.uniqueid }))
      .then((dto) => {
        this.rebindDeviationTeamMembersWrapper(dto);
        this.eventHelper.publish('settings-changed');
        this.toastHelper.toast('Team Member has been <b>added</b>.', 'Team Member added', ToastType.Success, ToastFormat.Html);
      });
  }

  removeMember(item: SystemUserWrapper): Promise<void> {
    return this.api.removeTeamMember(this.model.id, new RemoveTeamMember({ teamMemberId: item.userId }))
      .then((dto) => {
        this.rebindDeviationTeamMembersWrapper(dto);
        this.eventHelper.publish('settings-changed');
        this.toastHelper.toast('Team Member has been <b>removed</b>.', 'Team Member removed', ToastType.Success, ToastFormat.Html);
      });
  }

  private rebindDeviationTeamMembersWrapper(dto: IDeviationTeamDetailDTO) {
    this.deviationTeamDetailModel.membersWrapper = dto.members.map(x => ({
      user: this.businessTypesMapping.mapUserToUserContact(x),
      userId: x.id
    } as SystemUserWrapper));
  }

  createNewRegionDirector() {
    if (!this.regionDirectors.some(x => x.data.id == null)) {
      this.regionDirectors.unshift({
        data: {
          id: null,
          region: null,
          director: null,
          isActive: true
        } as DeviationTeamRegionDirectorDataModel,
        hasChanged: true,
        validationController: null
      } as DeviationTeamRegionDirectorModel)
      this.creatingNewRegionDirector = true;
    }
  }

  cancelCreateNewRegionDirector() {
    const newItemIndex = this.regionDirectors.findIndex(x => x.data.id == null);
    if (newItemIndex > -1)
      this.regionDirectors.splice(newItemIndex, 1);
    this.creatingNewRegionDirector = false;
  }

  async saveRegionDirector(item: DeviationTeamRegionDirectorModel) {
    await this.validationHelper.validate(item.validationController) && await this.saveRegionDirectorInternal(item);
  }

  async cancelRegionDirectorChanges(item: DeviationTeamRegionDirectorModel) {
    if (!item.hasChanged || await this.dialogHelper.confirm({ dialogTitle: 'Cancel your changes?', message: 'You haven\'t saved the form yet. If you proceed, your changes will be lost!', okTitle: 'Sure, do it!', cancelTitle: 'Back to form' })) {
      if (item.data.id) {
        item.cancelChanges();
      }
      else {
        this.cancelCreateNewRegionDirector();
      }
    }
  }

  async archiveRegionDirector(item: DeviationTeamRegionDirectorModel) {
    item.rebindData(this.deviationTeamsMapping.mapToDeviationTeamRegionDirectorDataModel(await this.api.archiveRegionDirector(this.model.id, new ChangeActiveRegionDirector({ regionDirectorId: item.data.id }))));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Region Director has been successfully <b>archived</b>.', 'Archived', ToastType.Success, ToastFormat.Html);
  }

  async restoreRegionDirector(item: DeviationTeamRegionDirectorModel) {
    item.rebindData(this.deviationTeamsMapping.mapToDeviationTeamRegionDirectorDataModel(await this.api.unarchiveRegionDirector(this.model.id, new ChangeActiveRegionDirector({ regionDirectorId: item.data.id }))));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Region Director has been successfully <b>restored</b>.', 'Restored', ToastType.Success, ToastFormat.Html);
  }

  private async saveRegionDirectorInternal(item: DeviationTeamRegionDirectorModel) {
    const data = this.getRegionDirectorSaveData(item.data);
    let dto = null as IDeviationTeamRegionDirectorDetailDTO;
    if (item.data.id)
      dto = await this.api.updateRegionDirector(this.model.id, new DeviationTeamRegionDirectorUpdateDTO({ ...data, id: item.data.id }));
    else {
      dto = await this.api.addRegionDirector(this.model.id, new DeviationTeamRegionDirectorCreateDTO(data));
      this.creatingNewRegionDirector = false;
    }
    item.rebindData(this.deviationTeamsMapping.mapToDeviationTeamRegionDirectorDataModel(dto));
    this.eventHelper.publish('settings-changed');
    this.toastHelper.toast('Region Director has been successfully <b>saved</b>.', 'Saved', ToastType.Success, ToastFormat.Html);
  }

  getRegionDirectorSaveData(model: DeviationTeamRegionDirectorDataModel): IDeviationTeamRegionDirectorUpdateBaseDTO {
    return {
      region: model.region,
      directorUniqueId: model.director.uniqueid
    }
  }

  @watch((x: DeviationTeamPanelCustomElement) => x.changed)
  private updateButons() {
    this.submitButtonVisible = this.changed;
    this.cancelButtonVisible = this.changed;
  }

  @computedFrom('model.teamLeader')
  get teamLeaderGroupLabel() {
    return ['Team Leader / bp employee name', this.model.teamLeader?.mail, this.model.teamLeader?.phone].filter(l => l != null);
  }

  usersLookupOptions = this.lookups.getUsersLookupOptions();
}
